import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import apiClient from "../../api/apiClient";

export default function Contact() {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    description: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const nameTrimmed = formData.name.trim();
    const emailTrimmed = formData.email.trim();
    const descriptionTrimmed = formData.description.trim();

    if (!nameTrimmed || !emailTrimmed || !descriptionTrimmed) {
      toast.error(t("contact.fillRequired", "Please fill in all required fields."));
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailTrimmed)) {
      toast.error(t("contact.invalidEmail", "Please enter a valid email address."));
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await apiClient.post("/api/v1/contact", {
        name: nameTrimmed,
        email: emailTrimmed,
        subject: formData.subject.trim() || undefined,
        description: descriptionTrimmed,
      });

      if (response.status === 200 || response.status === 201) {
        toast.success(
          t(
            "contact.successTitle",
            "Thank you! Your message has been sent successfully."
          )
        );
        setIsSubmitted(true);
        setFormData({
          name: "",
          email: "",
          subject: "",
          description: "",
        });
      } else {
        toast.error(
          t(
            "contact.sendError",
            "Failed to send message. Please try again or email us directly."
          )
        );
      }
    } catch (error: any) {
      const serverMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error?.message ||
        t(
          "contact.sendError",
          "Failed to send message. Please try again or email us directly."
        );
      toast.error(serverMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResetForm = () => {
    setIsSubmitted(false);
    setFormData({
      name: "",
      email: "",
      subject: "",
      description: "",
    });
  };

  return (
    <>
      {/* Banner */}
      <div className="relative">
        <div
          className="bg-cover bg-center text-center text-white py-32 md:py-40 px-4 relative overflow-hidden"
          style={{
            backgroundImage: `url("./images/innerb.jpg")`,
          }}
        >
          <div className="absolute inset-0 bg-[#020220]/40 backdrop-blur-[1px]"></div>
          <div className="relative z-10 max-w-4xl mx-auto">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight mb-3">
              {t("contact.bannerTitle", "Contact")}
            </h1>
            <p className="text-sm sm:text-base md:text-lg text-white/90 max-w-2xl mx-auto font-light">
              {t("contact.subtitle", "Fill out this brief form and we'll be back in touch with more information.")}
            </p>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="bg-[#F8FAFC] py-12 md:py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
            
            {/* Left Column: Form / Success Card */}
            <div className="lg:col-span-7 bg-white rounded-2xl p-6 sm:p-8 md:p-10 shadow-sm border border-[#E2E8F0] transition-all">
              {isSubmitted ? (
                <div className="text-center py-10 px-4">
                  <div className="w-16 h-16 bg-[#037897]/10 text-[#037897] rounded-full flex items-center justify-center mx-auto mb-5">
                    <svg
                      className="w-8 h-8"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                  <h3 className="text-2xl font-bold text-[#020220] mb-3">
                    {t("contact.successTitle", "Message Sent Successfully!")}
                  </h3>
                  <p className="text-[#607D8B] max-w-md mx-auto mb-8 text-sm sm:text-base leading-relaxed">
                    {t(
                      "contact.successDesc",
                      "Thank you for reaching out to Centre SEVA. We have received your inquiry and our team will get back to you shortly."
                    )}
                  </p>
                  <button
                    onClick={handleResetForm}
                    className="inline-flex items-center justify-center bg-[#B4804A] hover:bg-[#9B6E3E] text-white font-medium px-6 py-3 rounded-lg shadow-sm transition-colors text-sm sm:text-base"
                  >
                    {t("contact.sendAnother", "Send Another Message")}
                  </button>
                </div>
              ) : (
                <>
                  <div className="mb-6">
                    <span className="text-xs font-bold uppercase tracking-wider text-[#037897] bg-[#037897]/10 px-3 py-1 rounded-full">
                      Centre SEVA
                    </span>
                    <h2 className="font-semibold text-2xl sm:text-3xl md:text-4xl text-[#020220] tracking-tight mt-3 mb-2">
                      {t("contact.title", "Get in touch today")}
                    </h2>
                    <p className="text-[#607D8B] text-sm sm:text-base">
                      {t("contact.subtitle", "Fill out this brief form and we'll be back in touch with more information.")}
                    </p>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-5" noValidate>
                    {/* Name */}
                    <div>
                      <label
                        className="block text-[#475569] text-sm font-medium mb-1.5"
                        htmlFor="name"
                      >
                        {t("contact.nameLabel", "Name")} <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        disabled={isSubmitting}
                        className="w-full border-2 border-[#E0E4EC] bg-[#EDF2F6] text-[#263238] rounded-lg px-4 py-2.5 text-sm sm:text-base font-gilroy-medium focus:bg-white focus:border-[#037897] focus:outline-none transition-all disabled:opacity-60"
                        placeholder={t("contact.namePlaceholder", "Enter your name")}
                        required
                      />
                    </div>

                    {/* Email */}
                    <div>
                      <label
                        className="block text-[#475569] text-sm font-medium mb-1.5"
                        htmlFor="email"
                      >
                        {t("contact.emailLabel", "Email Address")} <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        disabled={isSubmitting}
                        className="w-full border-2 border-[#E0E4EC] bg-[#EDF2F6] text-[#263238] rounded-lg px-4 py-2.5 text-sm sm:text-base font-gilroy-medium focus:bg-white focus:border-[#037897] focus:outline-none transition-all disabled:opacity-60"
                        placeholder={t("contact.emailPlaceholder", "Enter your email")}
                        required
                      />
                    </div>

                    {/* Subject (Optional) */}
                    <div>
                      <label
                        className="block text-[#475569] text-sm font-medium mb-1.5"
                        htmlFor="subject"
                      >
                        {t("contact.subjectLabel", "Subject")}
                      </label>
                      <input
                        type="text"
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        disabled={isSubmitting}
                        className="w-full border-2 border-[#E0E4EC] bg-[#EDF2F6] text-[#263238] rounded-lg px-4 py-2.5 text-sm sm:text-base font-gilroy-medium focus:bg-white focus:border-[#037897] focus:outline-none transition-all disabled:opacity-60"
                        placeholder={t("contact.subjectPlaceholder", "What is this regarding? (Optional)")}
                      />
                    </div>

                    {/* Description / Message */}
                    <div>
                      <label
                        className="block text-[#475569] text-sm font-medium mb-1.5"
                        htmlFor="description"
                      >
                        {t("contact.descriptionLabel", "Message / Description")} <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        id="description"
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        disabled={isSubmitting}
                        className="w-full resize-none border-2 border-[#E0E4EC] bg-[#EDF2F6] text-[#263238] rounded-lg px-4 py-2.5 text-sm sm:text-base font-gilroy-medium focus:bg-white focus:border-[#037897] focus:outline-none transition-all disabled:opacity-60"
                        rows={5}
                        placeholder={t("contact.descriptionPlaceholder", "How can we help you today?")}
                        required
                      ></textarea>
                    </div>

                    {/* Submit Button */}
                    <div className="pt-2">
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#B4804A] hover:bg-[#9B6E3E] text-white font-semibold px-8 py-3 rounded-lg shadow-sm transition-all duration-200 disabled:opacity-60 cursor-pointer text-sm sm:text-base"
                      >
                        {isSubmitting ? (
                          <>
                            <svg
                              className="animate-spin h-5 w-5 text-white"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              ></circle>
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              ></path>
                            </svg>
                            <span>{t("contact.submitting", "Sending Message...")}</span>
                          </>
                        ) : (
                          <span>{t("contact.submit", "Send Message")}</span>
                        )}
                      </button>
                    </div>
                  </form>
                </>
              )}
            </div>

            {/* Right Column: Contact Details & Center Info */}
            <div className="lg:col-span-5 space-y-6">
              {/* Information Card */}
              <div className="bg-[#020220] text-white rounded-2xl p-6 sm:p-8 shadow-sm border border-[#1E1E45]">
                <h3 className="text-xl sm:text-2xl font-bold mb-3 tracking-tight">
                  {t("contact.infoTitle", "Contact Information")}
                </h3>
                <p className="text-[#9392C7] text-sm sm:text-base mb-6 leading-relaxed">
                  {t(
                    "contact.infoSubtitle",
                    "Have questions about our therapeutic programs, counseling, or online sessions? We are here to support your wellness journey."
                  )}
                </p>

                <div className="space-y-5">
                  {/* Email */}
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-[#037897]/20 text-[#037897] flex items-center justify-center shrink-0 mt-0.5">
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                        />
                      </svg>
                    </div>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wider text-[#9392C7]">
                        {t("contact.emailInfo", "Direct Email")}
                      </p>
                      <a
                        href="mailto:info@centre-seva.com"
                        className="text-white hover:text-[#037897] font-medium transition-colors text-sm sm:text-base block mt-0.5"
                      >
                        info@centre-seva.com
                      </a>
                    </div>
                  </div>

                  {/* Working Hours */}
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-[#037897]/20 text-[#037897] flex items-center justify-center shrink-0 mt-0.5">
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    </div>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wider text-[#9392C7]">
                        {t("contact.hoursInfo", "Hours & Consultations")}
                      </p>
                      <p className="text-white font-medium text-sm sm:text-base mt-0.5">
                        {t("contact.hoursDetail", "Monday – Friday: 9:00 AM – 6:00 PM (CET)")}
                      </p>
                    </div>
                  </div>

                  {/* Location */}
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-[#037897]/20 text-[#037897] flex items-center justify-center shrink-0 mt-0.5">
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                      </svg>
                    </div>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wider text-[#9392C7]">
                        {t("contact.locationInfo", "Consultation Practice")}
                      </p>
                      <p className="text-white font-medium text-sm sm:text-base mt-0.5">
                        {t("contact.locationDetail", "Switzerland & Global Online Telehealth")}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Social Links */}
                <div className="mt-8 pt-6 border-t border-[#3E3D68]/60 flex items-center gap-4">
                  <span className="text-xs font-semibold uppercase tracking-wider text-[#9392C7]">
                    Follow Us:
                  </span>
                  <div className="flex items-center gap-3">
                    <a
                      href="https://www.facebook.com/centreseva/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-9 h-9 rounded-lg bg-[#1B1B54] hover:bg-[#037897] flex items-center justify-center text-white transition-colors"
                      aria-label="Facebook"
                    >
                      <svg className="w-4 h-4 fill-current" viewBox="0 0 18 36">
                        <path d="M10.407 13.2143V9.59421C10.407 8.59514 11.1918 7.78416 12.1586 7.78416H13.9103V3.25903H10.407C7.50443 3.25903 5.152 5.68987 5.152 8.68918V13.2143H1.64868V17.7394H5.152V32.2198H10.407V17.7394H13.9103L15.662 13.2143H10.407Z" />
                      </svg>
                    </a>
                    <a
                      href="https://www.instagram.com/centre.seva?stkn=MWpvNXV4OGwzbG9qZQ%3D%3D"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-9 h-9 rounded-lg bg-[#1B1B54] hover:bg-[#037897] flex items-center justify-center text-white transition-colors"
                      aria-label="Instagram"
                    >
                      <svg className="w-4 h-4 fill-current" viewBox="0 0 27 27">
                        <path d="M18.3629 0.806641H8.34677C3.73779 0.806641 0 4.38192 0 8.79051V18.3712C0 22.7798 3.73779 26.355 8.34677 26.355H18.3629C22.9719 26.355 26.7097 22.7798 26.7097 18.3712V8.79051C26.7097 4.38192 22.9719 0.806641 18.3629 0.806641ZM24.2056 18.3712C24.2056 21.4531 21.5849 23.9599 18.3629 23.9599H8.34677C5.12479 23.9599 2.50403 21.4531 2.50403 18.3712V8.79051C2.50403 5.70861 5.12544 3.2018 8.34677 3.2018H18.3629C21.5849 3.2018 24.2056 5.70924 24.2056 8.79051V18.3712Z" />
                        <path d="M13.3553 7.19421C9.6677 7.19421 6.67786 10.0541 6.67786 13.5813C6.67786 17.1086 9.6677 19.9684 13.3553 19.9684C17.0429 19.9684 20.0327 17.1086 20.0327 13.5813C20.0327 10.0541 17.0429 7.19421 13.3553 7.19421ZM13.3553 17.5732C11.0547 17.5732 9.18189 15.7819 9.18189 13.5813C9.18189 11.3789 11.0547 9.58938 13.3553 9.58938C15.6559 9.58938 17.5287 11.3789 17.5287 13.5813C17.5287 15.7819 15.6559 17.5732 13.3553 17.5732Z" />
                      </svg>
                    </a>
                    <a
                      href="https://www.linkedin.com/company/centre-seva/posts/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-9 h-9 rounded-lg bg-[#1B1B54] hover:bg-[#037897] flex items-center justify-center text-white transition-colors"
                      aria-label="LinkedIn"
                    >
                      <svg className="w-4 h-4 fill-current" viewBox="0 0 26 25">
                        <path d="M5.71346 8.06458H0V24.0323H5.71346V8.06458Z" />
                        <path d="M21.2757 8.25158C21.2151 8.23456 21.1571 8.21529 21.0935 8.19941C21.0168 8.18353 20.9401 8.17049 20.8621 8.15858C20.559 8.10358 20.2265 8.06445 19.8367 8.06445C16.5059 8.06445 14.3933 10.2668 13.6972 11.1174V8.06445H7.98438V24.0322H13.6978V15.3225C13.6978 15.3225 18.0154 9.85572 19.8373 13.8709V24.0322H25.5495V13.2568C25.5489 10.8441 23.7301 8.83392 21.2757 8.25158Z" />
                        <path d="M5.58871 3.34696C5.58871 4.74981 4.33749 5.88729 2.79435 5.88729C1.25122 5.88729 0 4.74981 0 3.34696C0 1.94412 1.25122 0.806641 2.79435 0.806641C4.33749 0.806641 5.58871 1.94412 5.58871 3.34696Z" />
                      </svg>
                    </a>
                  </div>
                </div>
              </div>

              {/* Action Banner Card */}
              <div className="bg-[#EDF2F6] rounded-2xl p-6 sm:p-7 border border-[#E0E4EC]">
                <h4 className="text-lg font-bold text-[#020220] mb-2">
                  {t("contact.directBookTitle", "Ready to Start Your Program?")}
                </h4>
                <p className="text-[#607D8B] text-sm mb-5 leading-relaxed">
                  {t(
                    "contact.directBookDesc",
                    "Discover our personalized health condition programs and start your assessment today."
                  )}
                </p>
                <Link
                  to="/programs"
                  className="inline-flex items-center gap-2 text-sm font-bold text-[#037897] hover:text-[#025a72] transition-colors"
                >
                  <span>{t("contact.explorePrograms", "Explore Programs")}</span>
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M17 8l4 4m0 0l-4 4m4-4H3"
                    />
                  </svg>
                </Link>
              </div>
            </div>

          </div>
        </div>
      </div>
    </>
  );
}
